/**
 * Merchant Management Service
 * API functions for merchant management operations
 */
import { commonServerInstance } from '@/utilities/instances'

// Type definitions
export interface IMerchant {
  id: string
  company_name: string
  status: 'Active' | 'Inactive'
  email: string
  address: string
  billing_period: string
  type: string
  term: string
  last_billing_time: string
  created_at: string
  nickname: string
}

export interface IPartner {
  id: string
  name: string
  customLabel?: string
}

export interface IAccount {
  id: string
  username: string
}

export interface IMerchantDetail {
  parent: {
    address: string
    adr_line_1: string
    adr_line_2: string
    billing_period: string
    city: string
    contact_name: string
    country: string
    created_at: string
    currency: string
    email: string
    last_billing_time: string
    parent_merchant_formal_name: string
    parent_merchant_id: string
    parent_merchant_nickname: string
    phone: string
    state_province: string
    status: string
    term: number
    team: string
    type: string
    updated_at: string
    zip_postal: string
    team_id: string | number
    bef_auto_submit: boolean
    bef_auto_submit_updated_at: string | null
    // Optional until the backend ships the split guard — a detail response
    // without these keys must still type-check.
    split_bef_by_customer?: boolean
    split_bef_by_customer_updated_at?: string | null
  }
  customer: {
    data: Array<{
      id: string
      name: string
    }>
    total: number
  }
  authorized: Record<string, string>
}

export interface ICustomerBillingInfoFields {
  invoice_display_name: string
  contact_name: string
  email: string
  phone: string
  adr_line_1: string
  adr_line_2: string
  city: string
  state_province: string
  zip_postal: string
  country: string
}

export interface ICustomerBillingInfoResponse {
  customer_id: number
  customer_name: string
  partner_id: number
  is_complete: boolean
  missing_required_fields: string[]
  bill_to_notice: string
  billing_info: ICustomerBillingInfoFields
}

/**
 * Per-Merchant-ID billing-info completeness, keyed by Merchant ID as a string.
 *
 * An id the server omitted is **absent from the map**, which means "unknown" —
 * never `Incomplete`. Test for the key, not for a truthy value.
 */
export type CustomerBillingCompleteness = Record<string, boolean>

export interface IMerchantSearchParams {
  partnerId?: string
  status?: string
  page?: number
  per_page?: number
}

// Mock merchant details data is no longer used - replaced with API calls

/**
 * Fetch partners for AutoComplete options
 */
export const fetchMerchantSearchResult = async (params?: {
  page?: number
  per_page?: number
  id?: string
  status?: string
}) => {
  const response = await commonServerInstance.get('/settle/merchant', {
    params
  })
  return response?.data?.data
}

export const toggleMerchantStatus = async (params: {
  partner_id: number
  status: string
}) => {
  const response = await commonServerInstance.post(
    `/settle/merchant/change-status`,
    params
  )
  return response?.data?.data
}

/**
 * Fetch detailed merchant information including base info, customers, and KAs
 */
export const fetchMerchantDetail = async (params: {
  id: string
  page: number
  per_page: number
}): Promise<IMerchantDetail> => {
  const response = await commonServerInstance.get(`/settle/merchant/detail`, {
    params
  })
  return response?.data?.data
}

export const assignAccountToMerchant = async (data: {
  partner_id: string
  auth_ids: string
  team_id: string | number
}) => {
  const response = await commonServerInstance.post(
    `/settle/merchant/assign-users`,
    data
  )
  return response?.data?.data
}

export const fetchAvailablePartners = async () => {
  const response = await commonServerInstance.get(
    '/settle/merchant/team-partners'
  )
  return response?.data?.data
}

export const fetchTeamMembers = async (params: { id: string | number }) => {
  const response = await commonServerInstance.get('/settle/team/team-members', {
    params
  })
  return response?.data?.data
}

export const fetchTeamList = async () => {
  const response = await commonServerInstance.get('/settle/team')
  return response?.data?.data
}

export interface IMerchantCutoverConfig {
  read_only: boolean
  prm_url: string
}

export const fetchMerchantCutoverConfig =
  async (): Promise<IMerchantCutoverConfig> => {
    const response = await commonServerInstance.get(
      '/settle/merchant/cutover-config'
    )
    return response?.data?.data
  }

/**
 * Billing info maintained against one Merchant ID (sub-account).
 *
 * A Merchant ID whose billing info has never been entered is not an error: the
 * backend answers 200 with all ten fields as empty strings and
 * `is_complete: false`, so the caller never has to tell "never maintained"
 * apart from "saved blank".
 */
export const fetchCustomerBillingInfo = async (params: {
  customer_id: string
}): Promise<ICustomerBillingInfoResponse> => {
  try {
    const response = await commonServerInstance.get(
      '/settle/merchant/customer-billing-info',
      { params }
    )
    return response?.data?.data
  } catch (error) {
    console.error('Fetch Customer Billing Info Error:', error)
    throw error
  }
}

/**
 * Completeness for a batch of Merchant IDs — the same route as
 * `fetchCustomerBillingInfo`, in its second, **mutually exclusive** query form
 * (backend tech design §5.4.2: *"The same route takes a second, mutually
 * exclusive query form"*). 1–100 ids; statuses are 200 / 403 / 422 with no 404.
 *
 * Sending `customer_id` and `customer_ids` together — or neither — is `422000`,
 * so this is the only caller of the batch form and `fetchCustomerBillingInfo`
 * is the only caller of the single form. Do not merge them behind one optional
 * parameter; that is how both keys end up on one request.
 */
export const fetchCustomerBillingCompleteness = async (
  customerIds: string[]
): Promise<CustomerBillingCompleteness> => {
  try {
    const response = await commonServerInstance.get(
      '/settle/merchant/customer-billing-info',
      { params: { customer_ids: customerIds.join(',') } }
    )
    // The batch form answers `data.completeness`; `{}` is a valid body, not an
    // error. Reading `data` instead would hand the render path an object with a
    // single `completeness` key and every row would show as unknown.
    const raw = (response?.data?.data?.completeness ?? {}) as Record<
      string,
      unknown
    >
    // The one and only place a Merchant ID is coerced to a string.
    return Object.fromEntries(
      Object.entries(raw).map(([id, complete]) => [String(id), Boolean(complete)])
    )
  } catch (error) {
    console.error('Fetch Customer Billing Completeness Error:', error)
    throw error
  }
}
/**
 * Parameters for the merchant export endpoint (STLDEV-1456).
 * - select_all=true : export everything in the current filter range (intersected
 *   with the caller's scope); `ids` is omitted.
 * - select_all=false: export exactly the checked `ids`.
 * `id`/`status` mirror the current list filters (carried through in select-all mode).
 */
export interface IMerchantExportParams {
  select_all: boolean
  ids?: number[]
  id?: string
  status?: string
}

/**
 * Export merchants. The backend uploads the .xlsx to S3 and returns a
 * short-lived presigned URL; we trigger the browser download from it.
 */
export const exportMerchants = async (params: IMerchantExportParams) => {
  const response = await commonServerInstance.post('/settle/merchant/export', {
    ...params
  })
  const url = response?.data?.data?.url
  if (url) {
    // Presigned S3 URL (cross-origin) — trigger the browser download. The
    // downloaded filename comes from the S3 object key's basename.
    const link = document.createElement('a')
    link.href = url
    document.body.appendChild(link)
    link.click()
    link.remove()
  }
  return response?.data?.data
}
